import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, X, ShieldCheck, FileCheck2, Brain, Layers, Play } from 'lucide-react';
import PreviewBanner from './PreviewBanner';

const painPoints = [
  'Project data scattered across spreadsheets, emails, and shared drives',
  'No real-time visibility of portfolio health until issues escalate',
  'Inconsistent delivery processes between project managers and teams',
  'CDM and compliance documentation incomplete or missing',
  'Stakeholders disconnected from project progress',
  'Teams lacking confidence navigating PFI, lifecycle, or contractual obligations',
];

const capabilities = [
  { icon: ShieldCheck, title: 'Stage-gated governance',  desc: 'Every project moves through your defined gates. Evidence captured, approvals logged — nothing slips.' },
  { icon: FileCheck2,  title: 'Audit-ready by default',  desc: 'Compliance, safety, commercial and quality evidence is built in — not bolted on. Ready for the board, the regulator, or the auditor.' },
  { icon: Brain,       title: 'AI-powered reporting',    desc: 'Board packs and exception reports compile themselves in minutes, not days. Narrative written. Risks surfaced. Decisions ready.' },
  { icon: Layers,      title: 'Bespoke or templated',    desc: 'Configure Videra to your exact way of working — or start with a proven sector workspace and trial it in 14 days.' },
];

const PreviewPlatform = () => (
  <div data-testid="preview-platform">
    <PreviewBanner label="Platform" livePath="/platform" liveLabel="Platform" />

    {/* HERO */}
    <section className="pt-16 pb-16 md:pt-24 md:pb-20 bg-slate-900 text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-xs uppercase tracking-[0.2em] text-blue-400 font-semibold mb-4">The platform</p>
        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.02]">Videra</h1>
        <p className="mt-6 text-xl md:text-2xl text-slate-300 font-light tracking-tight">
          The delivery operating system, <span className="text-blue-400">made by Keystone.</span>
        </p>
      </div>
    </section>

    {/* SOUND FAMILIAR? */}
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">The problem we solve</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 leading-tight">Sound familiar?</h2>
          <ul className="mt-8 space-y-4">
            {painPoints.map((p) => (
              <li key={p} className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-md bg-red-50 border border-red-100 text-red-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <X className="w-3.5 h-3.5" />
                </span>
                <p className="text-base md:text-lg text-slate-700 leading-snug">{p}</p>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-slate-900 text-white rounded-2xl p-10 md:p-14 text-center">
          <p className="text-sm uppercase tracking-[0.2em] text-blue-400 font-semibold mb-6">Built to solve exactly this</p>
          <p className="text-2xl md:text-3xl lg:text-4xl font-medium tracking-tight leading-[1.15]">
            One platform. Complete <span className="text-blue-400">visibility.</span> Real <span className="text-blue-400">control.</span>
          </p>
        </div>
      </div>
    </section>

    {/* CAPABILITIES — moved from old home */}
    <section className="py-20 md:py-28 bg-slate-50 border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-14">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">What Videra does</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 leading-tight">
            Twenty years of delivery practice — packaged as software.
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            One place to plan, govern, evidence and report across every project in your portfolio.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {capabilities.map((c) => (
            <div key={c.title} className="bg-white border border-slate-200 rounded-xl p-6 hover:border-slate-900 transition-colors">
              <div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-100 text-blue-600 flex items-center justify-center mb-4">
                <c.icon className="w-5 h-5" />
              </div>
              <h3 className="text-base font-medium text-slate-900 mb-2">{c.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-24 md:py-32 bg-slate-950 text-white text-center">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight">Ready to see Videra in action?</h2>
        <p className="mt-6 text-lg text-slate-400">Book a demo to see how Videra can transform your portfolio management.</p>
        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          <a href="https://demo.videra.app" className="inline-flex items-center justify-center bg-white text-slate-900 px-8 py-4 font-medium rounded-md hover:bg-slate-100 transition-all group">
            <Play className="mr-2 w-4 h-4" /> Try the demo <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
          <Link to="/contact" className="inline-flex items-center justify-center border border-white/40 text-white px-8 py-4 font-medium rounded-md hover:bg-white/10 transition-all">
            Speak to our team
          </Link>
        </div>
      </div>
    </section>
  </div>
);

export default PreviewPlatform;
